import { PriceTypeEnum } from '../../models/cartDetails/priceType.model';
import { BundleTypeEnum } from '../../models/cartDetails/bundle-type.enum';
import { SummaryPricesGroupModel } from './summary-prices-group.model';
import { PriceGroupEnum } from '../../models/cartDetails/price-group.enum';
import { Injectable } from '@angular/core';
import { CommonInfoService } from '../../cart-process/common-info.service';
import { DeliveryModeEnum } from '../../models/cartDetails/deliveryModeEnum.model';
import {
  IAdditionalBundleSet,
  IBundle,
  ICartDetails,
  IPrice,
  IPriceCycle,
  ISummaryInstallmentPricesList,
  ISummaryPrice
} from '../../interfaces/cart-details.interface';
import SlotService from '../../cart-process/slot.service';
import SlotModel from '../../models/slot.model';
import { DeliveryFormOutputState, DeliveryFormStateService } from '../../customer-form/form-state/delivery-form-state.service';
import { IdentityConfirmationTypeEnum } from '../../models/cartDetails/identityConfirmationType.enum';

@Injectable()
export class SummaryPricesService {

  public isServiceTypeMix: boolean;
  public deliveryCost: string;
  public summaryPricesList: SummaryPricesGroupModel[] = [];
  public summaryPriceTotalMonthly: SummaryPricesGroupModel[] = [];
  public summaryPriceTotalOnce: SummaryPricesGroupModel[] = [];
  public discountInformation: string;
  public deliveryCurrencySymbol: string;
  public isMultiSlots: boolean;
  public termOfServiceNumber: number;
  private taxName: string;

  constructor(
    private commonInfoService: CommonInfoService,
    private slotService: SlotService,
    private deliveryFormState: DeliveryFormStateService,
  ) {
  }

  public getDevicesAndAccessoriesPrice(): number {
    return this.summaryPricesList
      .filter((item: SummaryPricesGroupModel) => {
        return item.group === PriceGroupEnum.DEVICES_AND_ACCESSORIES && item.isOnce;
      })
      .map((item: SummaryPricesGroupModel) => {
        return +item.getMainPrice();
      })[0];
  }

  public preparePrices(cartDetails: ICartDetails, commonInfo: CommonInfoService, deliveryCost?: IPrice): void {
    const summaryInstallmentPricesList = this.getRetentionDevicePrices(cartDetails, commonInfo.isRetentionOffer);
    let filteredSummaryPricesList: SummaryPricesGroupModel[] = [];

    this.summaryPricesList = [];
    this.summaryPriceTotalMonthly = [];
    this.summaryPriceTotalOnce = [];
    this.isServiceTypeMix = commonInfo.isServiceTypeMix;
    this.discountInformation = cartDetails.discountInformation;
    this.isMultiSlots = this.slotService.getSlots().length > 1;
    this.taxName = commonInfo.taxName;

    if (this.commonInfoService.pageStep === 3) {
      this.discountInformation = cartDetails.selectedDiscountInformation;
    }

    // parametr potrzebny dla cen schodkowych
    const bundle = cartDetails.bundles.find((bundle: IBundle) => bundle.bundleType === BundleTypeEnum.OFFER);
    this.termOfServiceNumber = bundle ? bundle.offer.termOfServiceNumber : 0;


    if (deliveryCost) {
      this.setDeliveryCost(deliveryCost);
    }

    filteredSummaryPricesList = this.convertToSummaryPricesGroupModel(cartDetails.summaryPrices, commonInfo.isRetentionMultiOffer, summaryInstallmentPricesList);
    this.summaryPricesList = filteredSummaryPricesList;
  }

  private setDeliveryCost(deliveryCost: IPrice): void {
    this.deliveryCost = deliveryCost[this.taxName];

    this.deliveryFormState.subscribe((deliveryFormOutputState: DeliveryFormOutputState) => {
      const isCourierMethod = deliveryFormOutputState.deliveryModeName === DeliveryModeEnum.COURIER;
      const offlineIdentityConfirmation = deliveryFormOutputState.identityConfirmationType === IdentityConfirmationTypeEnum.OFFLINE;
      const nonIdentityConfirmation = !deliveryFormOutputState.identityConfirmationType;

      this.deliveryCost = isCourierMethod
      ? deliveryCost[this.taxName]
      : nonIdentityConfirmation && !this.commonInfoService.isOtherSalesOffer || offlineIdentityConfirmation
        ? '0.0'
        : deliveryCost[this.taxName];
    });
  }

  private getRetentionDevicePrices(cartDetails: ICartDetails, isRetentionOffer: boolean): any[] {
    const summaryInstallmentPricesList = [];
    cartDetails.bundles.forEach((bundle: IBundle) => {
      const offer = bundle.offer;
      const isSlotWithDevice = bundle.device?.length;

      if (isSlotWithDevice && isRetentionOffer) {
        const handsetOffer: ISummaryInstallmentPricesList = {
          deviceName: '',
          offerChangePrices: {},
          newContractPrices: {}
        };

        handsetOffer.deviceName = bundle.device.find(device => device.name)?.name;
        handsetOffer.offerChangePrices = offer.offerChangePrices;
        handsetOffer.newContractPrices = offer.newContractPrices;

        summaryInstallmentPricesList.push(handsetOffer);
      }
    });

    return summaryInstallmentPricesList;
  }

  private getSummaryAddonPrice(additionalBundles): IPriceCycle[] | [] {
    let summaryAddonPrice = [];
    additionalBundles?.forEach((additionalBundle: IAdditionalBundleSet) => {
      const additionalBundlesOfferPrice = additionalBundle?.bundles.find(
        (bundle: IBundle) => bundle.offer).offer.newContractPrices.summaryPriceWithAddons;

      summaryAddonPrice = [additionalBundlesOfferPrice.find((price: IPriceCycle) => price.price)];
    });

    return summaryAddonPrice;
  }

  private convertToSummaryPricesGroupModel(summaryPrices, isRetentionMultiOffer, summaryInstallmentPricesList): SummaryPricesGroupModel[] | [] {
    let filteredSummaryPricesList = [];

    summaryPrices.forEach((pricesGroup: ISummaryPrice) => {
      if (pricesGroup.group !== PriceGroupEnum.TOTAL_PRICE) {
        filteredSummaryPricesList.push(
          new SummaryPricesGroupModel(
            pricesGroup, this.taxName, this.isRetentionBeforeEOP, isRetentionMultiOffer, summaryInstallmentPricesList)
          );
      } else {
        if (pricesGroup.type === PriceTypeEnum.MONTHLY) {
          this.summaryPriceTotalMonthly.push(
            new SummaryPricesGroupModel(
              pricesGroup, this.taxName, this.isRetentionBeforeEOP, isRetentionMultiOffer, summaryInstallmentPricesList
            ));
        } else {
          this.summaryPriceTotalOnce.push(new SummaryPricesGroupModel(pricesGroup, this.taxName));
        }
      }
    });

    filteredSummaryPricesList.sort((prev: SummaryPricesGroupModel, next: SummaryPricesGroupModel) => {
      if (prev.isMonthly && next.isOnce) {
        return -1;
      } else if (prev.isOnce && next.isMonthly) {
        return +1;
      }

      return 0;
    });

    // remove duplicated objects
    filteredSummaryPricesList = filteredSummaryPricesList.filter(
      (v: SummaryPricesGroupModel, index: number, arr: SummaryPricesGroupModel[]) => {
      return arr.findIndex(t => (t.group === v.group && (t.title === v.title))) === index;
    });

    return filteredSummaryPricesList;
  }

  public get isRetentionBeforeEOP(): boolean {
    const slots = this.slotService.getAllSlots();
    const isBeforeEOP = !!slots.find((slot: SlotModel) => slot.oldContractEndingDate);

    return isBeforeEOP && this.commonInfoService.isRetentionOffer;
  }
}
